/**
 * BrandDivider — Phase D section handoff separator.
 *
 * Thin horizontal rule in brand-purple `--pp-purple` (same token the
 * SharedNav 3px rule and CTA band use). Sparingly placed at major
 * structural handoffs on public pages — the whole point is
 * scarcity, so cap yourself at 1–3 per page.
 *
 * Two variants:
 *   `variant="full"`     (default) — full-width, spans the page bleed.
 *   `variant="content"`  — bounded to the standard content wrap
 *                          (`pp-wrap` — max 1200px, matches every
 *                          section's inner column width) so the line
 *                          aligns with the surrounding copy instead
 *                          of running to the viewport edge.
 *
 * Semantic HTML: `<hr>` with `aria-hidden="true"` (purely decorative
 * — no meaningful role announcement needed; the section handoff is
 * already implied by adjacent `<section>` boundaries + headings).
 *
 * Sizing:
 *   - `2px` thick — reads as a hairline on desktop and mobile alike.
 *     3px was tried and felt heavy next to the SharedNav bottom rule;
 *     2px is deliberate visual separation from the nav's own weight.
 *   - `my-14 md:my-20` — matches the section vertical rhythm used
 *     across the public pages (`py-14 md:py-20` on section wrappers).
 *
 * Color source-of-truth: `var(--pp-purple)` — identical token to
 * the SharedNav bottom rule + About page CTA band. A theme edit
 * updates every purple surface in lockstep.
 */
import React from "react";
import { cn } from "@/lib/utils";

export default function BrandDivider({
  variant = "full",
  className = "",
  testId = "brand-divider",
}) {
  const isContent = variant === "content";
  return (
    <hr
      aria-hidden="true"
      role="separator"
      className={cn(
        // Thickness + color come from Tailwind utilities bound to the
        // shared `pp.purple` token (see `tailwind.config.js`). No
        // hardcoded hex literals in this component — a theme edit to
        // `--pp-purple` propagates automatically. `border-0` first so
        // the default UA `<hr>` groove is stripped; `border-t-2` +
        // `border-solid` + `border-pp-purple` then paint the rule.
        "border-0 border-t-2 border-solid border-pp-purple my-14 md:my-20",
        // Content-width variant is bounded to the standard page column
        // (max-w-[1200px] mirrors `.pp-wrap`) and centered with
        // horizontal auto margins. Full-width leaves the element to
        // span the parent (which is the viewport for section-level
        // handoffs).
        isContent && "mx-auto w-full max-w-[1200px] px-6",
        className,
      )}
      data-testid={testId}
      data-variant={variant}
    />
  );
}
